import React, { useState } from 'react';
import { Bell, Send, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { sendPushNotification } from '../lib/notifications';

interface NotificationPanelProps {
  className?: string;
}

export function NotificationPanel({ className = '' }: NotificationPanelProps) {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [url, setUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);

  const handleSendNotification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !message) return;

    try {
      setSending(true);
      setError(null);
      setSuccess(false);
      setDebugInfo(null);

      console.log('Starting notification send process...');

      // Get all push subscriptions
      const { data: subscriptions, error: subError } = await supabase
        .from('push_subscriptions')
        .select('id, user_id, subscription');

      if (subError) {
        console.error('Database error:', subError);
        throw new Error(`Database error: ${subError.message}`);
      }

      console.log('Found subscriptions:', subscriptions?.length || 0);

      if (!subscriptions?.length) {
        throw new Error('No subscribers found. Users need to allow notifications first.');
      }

      setDebugInfo({
        subscriptionsFound: subscriptions.length,
        sendingTo: subscriptions.length
      });

      // Send notification to each subscription and track failures
      const results = await Promise.allSettled(
        subscriptions.map(async (sub, index) => {
          console.log(`Sending to subscription ${index + 1}...`);
          
          let subscription;
          try {
            subscription = typeof sub.subscription === 'string' 
              ? JSON.parse(sub.subscription) 
              : sub.subscription;
          } catch (parseError) {
            console.error('Failed to parse subscription:', parseError);
            throw new Error(`Invalid subscription format for user ${index + 1}`);
          }

          const result = await sendPushNotification(
            subscription,
            {
              title,
              body: message,
              url: url || undefined,
              actions: [
                {
                  action: 'view',
                  title: 'View Details'
                }
              ]
            }
          );

          return {
            subscriptionId: sub.id,
            userId: sub.user_id,
            success: true,
            result
          };
        })
      );

      // Analyze results and collect failed subscription IDs
      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.filter(result => result.status === 'rejected').length;
      
      const failedSubscriptionIds = results
        .map((result, index) => ({
          result,
          subscription: subscriptions[index]
        }))
        .filter(({ result }) => result.status === 'rejected')
        .map(({ subscription }) => subscription.id);

      console.log('Notification results:', { successful, failed, failedSubscriptionIds });

      // Clean up failed subscriptions automatically
      if (failedSubscriptionIds.length > 0) {
        console.log('Cleaning up failed subscriptions...');
        const { error: deleteError } = await supabase
          .from('push_subscriptions')
          .delete()
          .in('id', failedSubscriptionIds);

        if (deleteError) {
          console.error('Failed to clean up invalid subscriptions:', deleteError);
        } else {
          console.log(`Cleaned up ${failedSubscriptionIds.length} invalid subscriptions`);
        }
      }

      if (successful > 0) {
        setSuccess(true);
        setDebugInfo(prev => ({
          ...prev,
          successful,
          failed,
          total: results.length,
          cleanedUp: failedSubscriptionIds.length
        }));
        
        // Clear form on success
        setTitle('');
        setMessage('');
        setUrl('');
      }

      if (failed > 0) {
        const failures = results
          .filter(result => result.status === 'rejected')
          .map(result => (result as PromiseRejectedResult).reason.message);
        
        console.error('Some notifications failed:', failures);
        
        if (successful === 0) {
          throw new Error(`All notifications failed. First error: ${failures[0]}`);
        } else {
          setError(`${failed} out of ${results.length} notifications failed (invalid subscriptions cleaned up automatically)`);
        }
      }

    } catch (err) {
      console.error('Error sending notification:', err);
      
      let errorMessage = 'Failed to send notification';
      
      if (err instanceof Error) {
        if (err.message.includes('Failed to fetch')) {
          errorMessage = 'Network error: Could not connect to notification service. Check if the push-notification edge function is deployed.';
        } else if (err.message.includes('No subscribers')) {
          errorMessage = 'No subscribers found. Users need to allow notifications first.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleCleanupInvalidSubscriptions = async () => {
    try {
      setCleaning(true);
      setError(null);
      
      // Call the cleanup function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cleanup-push-subscriptions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          }
        }
      );

      const data = await response.json();
      
      if (response.ok) {
        setSuccess(true);
        setDebugInfo({ 
          cleanupResult: `Cleaned up ${data.removedCount || 0} invalid subscriptions`,
          remainingSubscriptions: data.remainingCount || 0
        });
      } else {
        throw new Error(data.error || 'Cleanup failed');
      }
    } catch (err) {
      console.error('Cleanup failed:', err);
      setError(err instanceof Error ? err.message : 'Cleanup failed');
    } finally {
      setCleaning(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setSending(true);
      setError(null);
      
      // Test the edge function directly
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/push-notification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            subscription: {
              endpoint: 'https://fcm.googleapis.com/fcm/send/test',
              keys: {
                p256dh: 'BKjw1gTVOO5yNmH8NRGfG4cL5QvOps1EY7iD3qJ2kM9xR2f6K8nL7U4S3vP9eT1wQ2zA5B6C7D8E9F0G1H2I3J4K5L6M7N=',
                auth: 'dGVzdC1hdXRoLWtleS0xMjM0'
              }
            },
            notification: {
              title: 'Connection Test',
              body: 'Testing edge function connectivity'
            }
          })
        }
      );

      const data = await response.json();
      
      if (response.ok) {
        setSuccess(true);
        setDebugInfo({ testResult: 'Edge function is working correctly' });
      } else {
        throw new Error(data.error || 'Test failed');
      }
    } catch (err) {
      console.error('Test failed:', err);
      setError(err instanceof Error ? err.message : 'Test failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`bg-white rounded-2xl shadow-sm p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-semibold text-gray-800">Send Push Notification</h3>
          <p className="text-sm text-gray-500">Send notifications to all subscribed users</p>
        </div>
        <Bell className="text-blue-500" size={24} />
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
          <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={16} />
          <div className="text-red-600 text-sm">
            <p className="font-medium">Error:</p>
            <p>{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-2">
          <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={16} />
          <div className="text-green-600 text-sm">
            <p className="font-medium">Success!</p>
            {debugInfo && (
              <p>
                {debugInfo.successful ? 
                  `Sent to ${debugInfo.successful}/${debugInfo.total} subscribers${debugInfo.cleanedUp ? ` (${debugInfo.cleanedUp} invalid subscriptions removed)` : ''}` : 
                  debugInfo.testResult || debugInfo.cleanupResult || 'Operation completed successfully'
                }
              </p>
            )}
          </div>
        </div>
      )}

      {debugInfo && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-800 text-sm font-medium">Debug Info:</p>
          <pre className="text-blue-700 text-xs mt-1 overflow-auto">
            {JSON.stringify(debugInfo, null, 2)}
          </pre>
        </div>
      )}

      <form onSubmit={handleSendNotification} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Notification Title"
            className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Message
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Notification Message"
            className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-500 min-h-[100px]"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            URL (Optional)
          </label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com"
            className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-500"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            type="submit"
            disabled={sending || cleaning || !title || !message}
            className="py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            <Send size={18} />
            <span>{sending ? 'Sending...' : 'Send Notification'}</span>
          </button>
          
          <button
            type="button"
            onClick={handleTestConnection}
            disabled={sending || cleaning}
            className="py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Test Connection
          </button>
        </div>

        <div className="pt-4 border-t">
          <button
            type="button"
            onClick={handleCleanupInvalidSubscriptions}
            disabled={sending || cleaning}
            className="w-full py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            <Trash2 size={18} />
            <span>{cleaning ? 'Cleaning...' : 'Clean Up Invalid Subscriptions'}</span>
          </button>
          <p className="text-xs text-gray-500 mt-1 text-center">
            Remove expired or invalid push subscriptions from the database
          </p>
        </div>
      </form>
    </div>
  );
}